import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import SEOHead from '@/components/SEOHead';

export const faqs = [
  {
    question: 'What is BuildersLab?',
    answer: 'BuildersLab is a vibe coding platform that helps you build websites and mobile apps within minutes using AI.'
  },
  {
    question: 'How does the AI Builder work?',
    answer: 'Our AI Builder uses advanced artificial intelligence to generate professional websites in minutes. Simply describe your website needs, and our AI will create a fully functional, beautiful website that you can customize further.'
  },
  {
    question: 'What services do you offer?',
    answer: 'We offer a range of services including website development, web application development, mobile app development, UI/UX design, SEO optimization, and custom software solutions. We cater to both startups and established businesses.'
  },
  {
    question: 'How long does it take to build a custom website?',
    answer: 'Timeline varies based on project complexity. A simple website can take 1-2 weeks, while more complex applications may take 4-8 weeks or more. We\'ll provide a detailed timeline during our initial consultation.'
  },
  {
    question: 'Do you provide ongoing support and maintenance?',
    answer: 'Yes! We offer ongoing support and maintenance packages to ensure your website stays updated, secure, and performs optimally. We can discuss support options that fit your needs.'
  },
  {
    question: 'What is your pricing structure?',
    answer: 'Pricing varies based on project scope and requirements. The AI Builder offers instant pricing, while custom projects are quoted individually. Contact us for a detailed proposal tailored to your specific needs.'
  },
  {
    question: 'Can you help with SEO?',
    answer: 'Absolutely! We offer comprehensive SEO services including keyword research, on-page optimization, technical SEO, content strategy, and ongoing SEO management to help improve your search engine rankings.'
  },
  {
    question: 'Do you work with international clients?',
    answer: 'Yes, we work with clients globally. We\'re experienced in remote collaboration and can accommodate different time zones to ensure smooth communication throughout your project.'
  }
];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative overflow-hidden pt-20 pb-24">
      <SEOHead
        title="Frequently Asked Questions"
        description="Got questions? Find answers to the most common questions about BuildersLab's AI website builder, pricing, features, and support."
        ogUrl="https://builderslab.net/FAQs"
        jsonLd={FAQ_SCHEMA}
      />
      {/* Background Ambience */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_left,rgba(249,115,22,0.05)_0%,transparent_70%)]" />
        <div className="absolute bottom-1/4 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_right,rgba(59,130,246,0.05)_0%,transparent_70%)]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 mt-16">
        
        {/* Header Section */}
        <div
         
         
         
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
            <span className="text-orange-400 text-sm font-semibold tracking-wide">Knowledge Base</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.1] mb-6">
            Frequently Asked <br />
            <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-amber-400 bg-clip-text text-transparent">
              Questions
            </span>
          </h1>
          
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about BuildersLab, our AI Builder, and custom engineering services.
          </p>
        </div>

        {/* FAQ Accordions */}
        <div className="space-y-4 mb-20">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
               
               
               
                className={`rounded-2xl border backdrop-blur-md overflow-hidden transition-all duration-300 ${
                  isOpen 
                    ? 'bg-zinc-900/80 border-orange-500/30 shadow-[0_0_30px_rgba(249,115,22,0.05)]' 
                    : 'bg-zinc-900/30 border-white/5 hover:border-white/10 hover:bg-zinc-900/50'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full px-6 py-6 flex items-center justify-between text-left group"
                >
                  <span className={`font-semibold text-lg transition-colors pr-8 ${isOpen ? 'text-white' : 'text-zinc-300 group-hover:text-white'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                    isOpen ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-zinc-500 group-hover:bg-white/10 group-hover:text-zinc-300'
                  }`}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                
                <AnimatePresence>
                  {isOpen && (
                    <div
                     
                     
                     
                     
                    >
                      <div className="px-6 pb-6 text-zinc-400 leading-relaxed text-base pt-2">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <div
         
         
         
         
          className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-zinc-900/80 to-zinc-950 border border-white/10 p-10 text-center"
        >
          {/* Subtle glow inside CTA */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[150px] bg-orange-500/10 blur-[50px] pointer-events-none" />
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-white mb-3">
              Still have questions?
            </h3>
            <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
              Can't find the answer you're looking for? Reach out to our team and we'll get back to you with lightning speed.
            </p>
            <Link
              to={createPageUrl('Contact')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-zinc-950 rounded-xl hover:bg-zinc-200 transition-all font-bold group shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
            >
              Contact Us →
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}